import { i18n } from '../shared/i18n'

export interface HttpError extends Error {
  statusCode: number
}

function createHttpError(message: string, statusCode: number): HttpError {
  const error = new Error(message)
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- Required for Error mutation
  ;(error as HttpError).statusCode = statusCode
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- Error mutation for HttpError
  return error as HttpError
}

/**
 * Server fields that explain a refusal to the seller, most user-facing first. Sellers are end
 * users, so the Korean `userMessage` leads; the admin BO reads the diagnostic `detail` first
 * instead (API 오류 메시지 규칙 §1) — the two orders differ on purpose.
 */
const USER_FACING_FIELDS = ['userMessage', 'detail', 'message'] as const

function pickFirstText(source: Record<string, unknown>, fields: readonly string[]): string | null {
  for (const field of fields) {
    const value = source[field]
    if (typeof value === 'string' && value.trim() !== '') return value
  }
  return null
}

export function handleSdkError(error: unknown): HttpError {
  if (error instanceof Error) {
    if ('statusCode' in error && typeof error.statusCode === 'number') {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- Validated statusCode exists
      return error as HttpError
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- Error mutation for HttpError
    ;(error as HttpError).statusCode = 500
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- Error mutation for HttpError
    return error as HttpError
  }

  if (typeof error === 'object' && error !== null) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- Type narrowed by object check
    const errorObj = error as Record<string, unknown>
    // A problem+json body has no `message`, so reading `title` first surfaced the bare status
    // name ("Bad Request", "Conflict") in Refine's error notification instead of the reason.
    const message =
      pickFirstText(errorObj, [...USER_FACING_FIELDS, 'title']) ??
      i18n.t('common.errors.errorOccurred')
    const statusCode =
      (typeof errorObj.statusCode === 'number' && errorObj.statusCode) ||
      (typeof errorObj.status === 'number' && errorObj.status) ||
      500
    return createHttpError(message, statusCode)
  }

  return createHttpError(i18n.t('common.errors.unknownError'), 500)
}

/**
 * Pulls the server's user-facing explanation out of an RFC7807 problem response
 * ("합배송 대상에 종료되지 않은 방송의 주문이 있습니다", "배송지가 서로 달라…").
 * Returns null when the payload carries no readable message, so callers can show their own text.
 */
export function extractUserFacingMessage(error: unknown): string | null {
  if (typeof error === 'object' && error !== null) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- Type narrowed by object check
    return pickFirstText(error as Record<string, unknown>, USER_FACING_FIELDS)
  }
  return null
}
